// icons
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import InventoryIcon from '@mui/icons-material/Inventory';
import SchoolIcon from '@mui/icons-material/School';
import * as Sentry from '@sentry/react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';

import Page from 'components/Page';
// auth state components
import RequireAuth from 'components/RequireAuth';

// context providers
import { AuthProvider } from 'context/AuthContext';
import { SchoolProvider } from 'context/SchoolContext';
import { SelfScheduleContextProvider } from 'context/SelfScheduleContext';

// layout components
import { AdminLayout, ExternalLayout, MainLayout, StudentLayout } from 'layouts';
import { NavigationItem } from 'layouts/DashboardLayout';
import InstructorLayout from 'layouts/InstructorLayout';
import RootLayout from 'layouts/RootLayout';

// Admin layout pages
import CourseList from 'pages/Admin/CourseList';
import CoursePeriodRoster from 'pages/Admin/CoursePeriodRoster';
import CurriculumEditorPage from 'pages/Admin/CurriculumEditor/CurriculumEditorPage';
import CurriculumListPage from 'pages/Admin/CurriculumList/CurriculumListPage';
import { FormList } from 'pages/Admin/FormList';
import LegacyCalendar from 'pages/Admin/LegacyCalendar';
import ManageCourse from 'pages/Admin/ManageCourse';
import ManageForm from 'pages/Admin/ManageForm';
import ManagePackage from 'pages/Admin/ManagePackage';
import { ManageSection } from 'pages/Admin/ManageSection';
import PackageList from 'pages/Admin/PackageList';
import Reports from 'pages/Admin/Reports';
import SectionList from 'pages/Admin/SectionList';
import StudentProfile from 'pages/Admin/StudentProfile';
import TermsOfService from 'pages/Admin/TermsOfService';
import ExternalPackageRegistration from 'pages/External/ExternalPackageRegistration';
// Main layout pages
import About from 'pages/Main/About';
import Activate from 'pages/Main/Activate';
import Faq from 'pages/Main/Faq';
import ForgotPasswordCover from 'pages/Main/ForgotPasswordCover';
import Home from 'pages/Main/Home';
import LoginCover from 'pages/Main/LoginCover';
import NotFound from 'pages/Main/NotFound';
import PackageListing from 'pages/Main/PackageListing';
import ResetPasswordCover from 'pages/Main/ResetPasswordCover';
import VerifyEmailCover from 'pages/Main/VerifyEmailCover';
import CoursePlayerPage from 'pages/Student/CoursePlayer/CoursePlayerPage';
import Ledger from 'pages/Student/Ledger';
import MyCoursesPage from 'pages/Student/MyCourses/MyCoursesPage';
import SelfSchedule from 'pages/Student/SelfSchedule';
// Student layout pages
import StudentDashboard from 'pages/Student/StudentDashboard';
import StudentForms from 'pages/Student/StudentForms';
import StudentInfo from 'pages/Student/StudentInformation';

// setup react router integration with sentry
const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

// navigation items
const MainNavItems: Array<NavItem | NavDropDown> = [
  { title: 'Home', to: '/' },
  { title: 'FAQ', to: '/faq' },
  { title: 'About', to: '/about' },
];

const StudentNavItems: Array<NavItem | NavDropDown> = [
  { title: 'Student Dashboard', to: '/student/dashboard' },
  { title: 'My Courses', to: '/student/courses' },
  { title: 'Personal Information', to: '/student/personal-information' },
  { title: 'Forms', to: '/student/forms' },
  { title: 'Schedule Drives', to: '/student/self-schedule' },
  { title: 'Ledger', to: '/student/ledger' },
];

const AdminNavItems: NavigationItem[] = [
  {
    kind: 'header',
    title: 'Scheduling',
  },
  {
    kind: 'page',
    title: 'Calendar',
    to: '/admin/calendar',
    icon: <CalendarMonthIcon />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Course Management',
  },
  {
    kind: 'page',
    title: 'Courses',
    to: '/admin/courses',
    icon: <SchoolIcon />,
  },
  {
    kind: 'page',
    title: 'Curricula',
    to: '/admin/learning/curricula',
    icon: <SchoolIcon />,
  },
  {
    kind: 'page',
    title: 'Sections',
    to: '/admin/sections',
    icon: <SchoolIcon />,
  },
  {
    kind: 'page',
    title: 'Packages',
    to: '/admin/packages',
    icon: <InventoryIcon />,
  },
  // {
  //   kind: 'page',
  //   title: 'Terms of Service',
  //   to: '/admin/terms-of-service',
  //   icon: <AssignmentIcon />,
  // },
  // {
  //   kind: 'page',
  //   title: 'Email Editor',
  //   to: '/admin/email-editor',
  //   icon: <AssignmentIcon />,
  // },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Document Management',
  },
  {
    kind: 'page',
    title: 'Forms',
    to: '/admin/forms',
    icon: <InsertDriveFileIcon />,
  },
  {
    kind: 'divider',
  },
  {
    kind: 'header',
    title: 'Analytics',
  },
  {
    kind: 'page',
    title: 'Reports',
    to: '/admin/reports',
    icon: <AssessmentIcon />,
  },
  // {
  //   kind: 'divider',
  // },
  // {
  //   kind: 'header',
  //   title: 'Company Settings',
  // },
  // {
  //   kind: 'page',
  //   title: 'Terms of Service',
  //   to: '/admin/terms-of-service',
  //   icon: <GavelIcon />,
  // },
];

const InstructorNavItems: NavigationItem[] = [
  {
    kind: 'header',
    title: 'Scheduling',
  },
  {
    kind: 'page',
    title: 'Calendar',
    to: '/admin/calendar',
    icon: <CalendarMonthIcon />,
  },
];

const router = sentryCreateBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      {
        element: <MainLayout navBarItems={MainNavItems} />,
        children: [
          { index: true, element: <Home /> },
          { path: 'faq', element: <Faq /> },
          { path: 'about', element: <About /> },
          { path: 'package/:id', element: <PackageListing /> },
          { path: 'login', element: <LoginCover /> },
          { path: 'verify-email', element: <VerifyEmailCover /> },
          { path: 'forgot-password', element: <ForgotPasswordCover /> },
          { path: 'password-reset', element: <ResetPasswordCover /> },
          { path: 'activate', element: <Activate /> },
          { path: '*', element: <NotFound /> },
        ],
      },
      {
        path: '/student',
        element: <RequireAuth allowRoles={['STUDENT']} />,
        children: [
          {
            element: <StudentLayout navBarItems={StudentNavItems} />,
            children: [
              { path: 'dashboard', element: <StudentDashboard /> },
              { path: 'courses', element: <MyCoursesPage /> },
              { path: 'courses/:courseId', element: <CoursePlayerPage /> },
              { path: 'personal-information', element: <StudentInfo /> },
              { path: 'forms', element: <StudentForms /> },
              {
                path: 'self-schedule',
                element: (
                  <SelfScheduleContextProvider>
                    <SelfSchedule />
                  </SelfScheduleContextProvider>
                ),
              },
              { path: 'ledger', element: <Ledger /> },
              { index: true, element: <Navigate to="dashboard" /> },
              { path: '*', element: <NotFound /> },
            ],
          },
        ],
      },
      {
        path: '/admin',
        element: <RequireAuth allowRoles={['ADMIN']} />,
        children: [
          {
            element: <AdminLayout navItems={AdminNavItems} />,
            children: [
              {
                path: 'calendar',
                element: <LegacyCalendar />,
                handle: {
                  // breadcrumb: 'Calendar'
                },
              },
              {
                path: 'packages',
                element: <PackageList />,
              },
              {
                path: 'packages/:id',
                element: <ManagePackage />,
              },
              {
                path: 'courses',
                element: <CourseList />,
              },
              {
                path: 'courses/:id',
                element: <ManageCourse />,
              },
              {
                path: 'learning/curricula',
                element: <CurriculumListPage />,
              },
              {
                path: 'learning/curricula/:id',
                element: <CurriculumEditorPage />,
              },
              {
                path: 'sections',
                element: <SectionList />,
              },
              {
                path: 'sections/:id',
                element: <ManageSection />,
              },
              {
                path: 'periods/:id',
                element: <CoursePeriodRoster />,
              },
              {
                path: 'students/:id',
                element: <StudentProfile />,
              },
              {
                path: 'forms',
                element: <FormList />,
              },
              {
                path: 'forms/:id',
                element: <ManageForm />,
              },
              {
                path: 'reports',
                element: <Reports />,
              },
              {
                path: 'terms-of-service',
                element: <TermsOfService />,
              },
              {
                index: true,
                element: <Navigate to="calendar" />,
              },
              {
                path: '*',
                element: <NotFound />,
              },
            ],
          },
        ],
      },
      {
        path: '/instructor',
        element: <RequireAuth allowRoles={['ADMIN', 'INSTRUCTOR']} />,
        children: [
          {
            element: <InstructorLayout navItems={InstructorNavItems} />,
            children: [
              {
                path: 'calendar',
                element: <LegacyCalendar />,
              },
              {
                index: true,
                element: <Navigate to="calendar" />,
              },
              {
                path: '*',
                element: <NotFound />,
              },
            ],
          },
        ],
      },
      {
        path: '/external',
        element: <ExternalLayout />,
        children: [
          { path: 'login', element: <LoginCover /> },
          { path: 'forgot-password', element: <ForgotPasswordCover /> },
          { path: 'password-reset', element: <ResetPasswordCover /> },
          { path: 'package/:id', element: <ExternalPackageRegistration /> },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <Page>
      <AuthProvider>
        <SchoolProvider>
          <RouterProvider router={router} />
        </SchoolProvider>
      </AuthProvider>
    </Page>
  );
}
